import { useCallback, useEffect, useState } from 'react';

import { MeasurementFieldType, MeasurementFieldTypes, UnitConverter } from 'models';
import { isNear } from 'helpers/is-near';
import { notFalsey } from 'helpers/not-falsey';

type MultiUnitMeasurementRelayProps<TUnit extends string, TBaseUnit extends TUnit> = {
    fields: readonly MeasurementFieldType[];
    value: Record<MeasurementFieldType, number | null> & { unit?: TUnit };
    onChange: (newValue: Record<MeasurementFieldType, number | null>) => void;
    converter: UnitConverter<TUnit, TBaseUnit>;
    defaultUnit?: TUnit;
    displayUnits?: TUnit[];
};

type MultiUnitMeasurementRelayResult<TUnit extends string> = {
    fields: Record<
        MeasurementFieldType,
        {
            value: string;
            onChange: (input: string) => void;
        }
    >;
    unit: TUnit;
    cycleUnit?: () => void;
    convertValue: (value: number) => string;
};

export function useMultiUnitMeasurementRelay<TUnit extends string, TBaseUnit extends TUnit>({
    fields,
    value,
    onChange,
    converter,
    defaultUnit = converter.baseUnit,
    displayUnits = [...converter.getDisplayUnits()],
}: MultiUnitMeasurementRelayProps<TUnit, TBaseUnit>): MultiUnitMeasurementRelayResult<TUnit> {
    const baseUnit = value?.unit ?? converter.baseUnit;

    const [unit, setUnit] = useState<TUnit>(getBestUnit({ value, converter, defaultUnit, baseUnit }));

    const signature = getSignature(value);
    const [handledSignature, setHandledSignature] = useState(signature);
    const [emittedSignature, setEmittedSignature] = useState<string | null>(null);

    const isNewValue = signature !== handledSignature;
    const isOwnEdit = signature === emittedSignature;

    if (isNewValue) {
        setHandledSignature(signature);
        setEmittedSignature(null);

        if (!isOwnEdit) {
            setUnit(getBestUnit({ value, converter, defaultUnit, baseUnit }));
        }
    }

    useEffect(() => {
        if (!displayUnits.includes(unit)) {
            setUnit(getBestUnit({ value, converter, defaultUnit, baseUnit }));
        }
    }, [value, converter, defaultUnit, displayUnits, unit]);

    const convertValue = useCallback(
        (value: string | number | null | undefined) => {
            if (value === '' || value === null || value === undefined) {
                return '';
            }

            const convertValue = converter.convert({
                value: +value,
                from: baseUnit,
                to: unit,
            });

            if (isNear(convertValue.value, 0)) {
                return '0';
            }

            return convertValue.value.toFixed(6).replace(/\.?0*$/, '');
        },
        [converter, unit, baseUnit],
    );

    const parseValue = useCallback(
        (value: string) => {
            if (value === '' || isNaN(+value)) {
                return null;
            }

            return converter.convert({
                value: +value,
                from: unit,
                to: baseUnit,
            }).value;
        },
        [converter, unit, baseUnit],
    );

    const handleFieldChange = (field: MeasurementFieldType, input: string) => {
        const parsedValue = parseValue(input);
        const currentValue = value[field];

        if (currentValue === parsedValue) {
            return;
        }

        const newValue = { ...value, [field]: parsedValue };

        setEmittedSignature(getSignature(newValue));
        onChange(newValue);
    };

    const fieldsRecord = Object.fromEntries(
        fields.map((field) => [
            field,
            {
                value: convertValue(value[field]),
                onChange: (input: string) => handleFieldChange(field, input),
            },
        ]),
    ) as MultiUnitMeasurementRelayResult<TUnit>['fields'];

    const cycleUnit =
        displayUnits.length > 1
            ? () => {
                  setUnit(displayUnits[(displayUnits.indexOf(unit) + 1) % displayUnits.length]);
              }
            : undefined;

    return {
        fields: fieldsRecord,
        unit,
        cycleUnit,
        convertValue,
    };
}

const getSignature = (value: Partial<Record<MeasurementFieldType, number | null>> & { unit?: string }) =>
    JSON.stringify([value?.unit, ...MeasurementFieldTypes.map((fieldType) => value?.[fieldType] ?? null)]);

const getBestUnit = <TUnit extends string, TBaseUnit extends TUnit>({
    value,
    converter,
    defaultUnit,
    baseUnit,
}: {
    value: Record<MeasurementFieldType, number | null>;
    converter: UnitConverter<TUnit, TBaseUnit>;
    defaultUnit: TUnit;
    baseUnit: TBaseUnit;
}): TUnit => {
    const fieldQuantities = MeasurementFieldTypes.map((fieldType) => value?.[fieldType]).filter(notFalsey);

    const smallestQuantity =
        fieldQuantities.length > 0 ? fieldQuantities.reduce((minimum, next) => Math.min(minimum, next)) : null;

    if (smallestQuantity !== null) {
        return converter.toBest({ value: smallestQuantity, from: baseUnit }).unit;
    }

    return defaultUnit;
};
